import Dexie, { liveQuery, type EntityTable } from 'dexie'
import { formDataToJsonData } from './utils'
import { useContext } from 'react'
import { OfflineTaskContext } from './OfflineTaskManager'

const TASK_STATUS_DEFAULT: TaskStatus = {
  loading: false,
  saved: false,
  failed: false
}

export interface Element {
  id?: string
  url: string
  name: string
  elementId: string | number
  data: { [key: string]: object | string | number | boolean }
}

export interface Task {
  id: string
  url: string
  data: { [key: string]: object | string | number | boolean }
  method: 'post' | 'put'
  status: TaskStatus
  type: string
  owner: string
}

export interface TaskStatus {
  saved: boolean
  loading: boolean
  failed: boolean
}

export interface TaskToAdd {
  data: FormData
  url: string
  type: string
  owner: string
  method: Task['method']
}

const db = new Dexie('OfflineDatabase') as Dexie & {
  tasks: EntityTable<Task, 'id'>
  elements: EntityTable<Element, 'id'>
}

// Schema declaration:
db.version(1).stores({
  tasks: '++id, owner',
  elements: '++id, url, name, elementId'
})

const addElement = async (element: Element) => {
  await db.elements.add({
    ...element
  })
}

const updateElement = async ({ id, ...rest }: Element) => {
  await db.elements.where({ id }).modify(rest)
}

const getElement = async (name: string, url: string, elementId: string | number) => {
  const data = await db.elements.where({ url, name, elementId }).first()
  return data
}

const deleteElement = async (id: Element['id']) => {
  await db.elements.delete(id)
}

const addTask = async (taskToAdd: TaskToAdd) => {
  db.tasks.add({
    ...taskToAdd,
    data: await formDataToJsonData(taskToAdd.data),
    status: TASK_STATUS_DEFAULT
  })
}

const updateTask = async ({ id, ...restTask }: Task) => {
  db.tasks.where({ id }).modify(restTask)
}

const deleteAllTasks = async () => {
  db.tasks.clear()
}

const deleteOwnTasks = (owner: string) => {
  db.tasks.where('owner').equals(owner).delete()
}

const addOnwTask = (owner: string, taskToAdd: Omit<TaskToAdd, 'owner'>) => {
  return addTask({
    ...taskToAdd,
    owner
  })
}

const deleteTask = async (id: Task['id']) => {
  db.tasks.delete(id)
}

const useOfflineTool = () => {
  const offlineContext = useContext(OfflineTaskContext)

  return {
    addOwnTask: (taskToAdd: Omit<TaskToAdd, 'owner'>) =>
      addOnwTask(offlineContext.owner, taskToAdd),
    deleteOwnTasks: () => deleteOwnTasks(offlineContext.owner),
    updateTask,
    addElement,
    updateElement,
    getElement,
    deleteElement
  }
}

export type {}
export { db, addTask, updateTask, deleteAllTasks, deleteTask, useOfflineTool, deleteOwnTasks }
